import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Grid2 as Grid, FormControl, FormGroup, InputLabel } from '@mui/material';
import { withStyles } from '@mui/styles';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterMoment } from '@mui/x-date-pickers-pro/AdapterMoment';
import moment from 'moment';
// eslint-disable-next-line no-restricted-imports
import { useController, useFormContext } from 'react-hook-form-v7';
import { textFieldStyles } from '../../../Configurations/ExpenseEntryConfig';
import { COLORS, DATE_FORMATS } from '../../../../utils/Application_Constants';

const styles = (theme) => {
    const baseStyles = textFieldStyles(theme);
    return {
        datePicker: {
            width: '10em',
            ...baseStyles.root,
            '& .MuiIconButton-root': {
                color: COLORS.LT_JOURNEY,
            },
            '& .Mui-error': {
                color: COLORS.LT_PEPPER,
            },
        },
    };
};

const dateInputSlotProps = {
    popper: { sx: { zIndex: 1320 } },
    dialog: { sx: { zIndex: 1320 } },
    textField: {
        placeholder: 'mm/dd/yyyy',
        variant: 'standard',
        margin: 'none',
        InputLabelProps: { shrink: true },
        InputProps: {
            sx: { flexShrink: 1 },
            disableUnderline: true,
        },
        sx: { '& .MuiPickersInputBase-root': { mt: 1 } },
    },
    openPickerButton: { 'aria-label': 'Choose date' },
};

export const getDateRangePickerInputNames = (name) => {
    const inputNameFrom = `${name}.from`;
    const inputNameTo = `${name}.to`;
    return { inputNameFrom, inputNameTo };
};

const RANGE_ERROR_FROM = 'Start date cannot be greater than end date';
const RANGE_ERROR_TO = 'End date cannot be less than start date';

const validateDateField = (value) => {
    if (!value) return true;
    return moment(value).isValid() || 'Invalid date';
};

const toFormValue = (date) => {
    if (!date) {
        return null;
    }
    return moment(date).format(DATE_FORMATS.ISO_8601);
};

const ConnectedDateRangePicker = forwardRef(({ control, classes, name, disabled = false }, ref) => {
    const { getValues, setValue, trigger } = useFormContext();
    const [fromFocused, setFromFocused] = useState(false);
    const [toFocused, setToFocused] = useState(false);
    const [fromPickerValue, setFromPickerValue] = useState(null);
    const [toPickerValue, setToPickerValue] = useState(null);

    const { inputNameFrom, inputNameTo } = getDateRangePickerInputNames(name);
    const fieldNames = [inputNameFrom, inputNameTo];

    const {
        field: { value: rangeFrom, onChange: setFromDate },
        fieldState: { error: fromFieldError },
    } = useController({
        control,
        name: inputNameFrom,
        defaultValue: null,
        rules: {
            validate: {
                invalidDate: validateDateField,
                range: (value) => {
                    const to = getValues(inputNameTo);
                    if (value && to && moment(value).isAfter(moment(to))) {
                        return RANGE_ERROR_FROM;
                    }
                    return true;
                },
            },
        },
    });
    const {
        field: { value: rangeTo, onChange: setToDate },
        fieldState: { error: toFieldError },
    } = useController({
        control,
        name: inputNameTo,
        defaultValue: null,
        rules: {
            validate: {
                invalidDate: validateDateField,
                range: (value) => {
                    const from = getValues(inputNameFrom);
                    if (value && from && moment(value).isBefore(moment(from))) {
                        return RANGE_ERROR_TO;
                    }
                    return true;
                },
            },
        },
    });

    useEffect(() => {
        setFromPickerValue(rangeFrom ? moment(rangeFrom) : null);
    }, [rangeFrom]);

    useEffect(() => {
        setToPickerValue(rangeTo ? moment(rangeTo) : null);
    }, [rangeTo]);

    const commitFieldValue = (date, isFrom) => {
        const fieldName = isFrom ? inputNameFrom : inputNameTo;
        setValue(fieldName, toFormValue(date), { shouldValidate: true, shouldDirty: true });
    };

    const validateRange = () => {
        trigger(fieldNames);
    };

    const handleClear = () => {
        setFromPickerValue(null);
        setToPickerValue(null);
        setFromDate(null);
        setToDate(null);
        trigger(fieldNames, { shouldFocus: false });
    };

    useImperativeHandle(ref, () => ({
        clear: handleClear,
    }));

    const handleDateChange = (date, type) => {
        const isFrom = type === inputNameFrom;
        const setPickerValue = isFrom ? setFromPickerValue : setToPickerValue;
        const fieldName = isFrom ? inputNameFrom : inputNameTo;

        setPickerValue(date);

        if (!date) {
            setValue(fieldName, null, { shouldValidate: true, shouldDirty: true });
            validateRange();
        }
    };

    const handleFieldBlur = (isFrom) => {
        commitFieldValue(isFrom ? fromPickerValue : toPickerValue, isFrom);
        validateRange();

        if (isFrom) {
            setFromFocused(false);
        } else {
            setToFocused(false);
        }
    };

    const handleDateAccept = (date, isFrom) => {
        const setPickerValue = isFrom ? setFromPickerValue : setToPickerValue;
        setPickerValue(date);
        commitFieldValue(date, isFrom);
        validateRange();
    };

    return (
        <LocalizationProvider dateAdapter={AdapterMoment}>
            <FormControl
                component="fieldset"
                ref={ref}
                disabled={disabled}
                error={!!fromFieldError || !!toFieldError}
            >
                <FormGroup>
                    <Grid container spacing={2}>
                        <Grid size={6}>
                            <InputLabel shrink focused={fromFocused} htmlFor={inputNameFrom}>
                                From
                            </InputLabel>
                            <DatePicker
                                name={inputNameFrom}
                                data-testid={`${inputNameFrom}-input`}
                                value={fromPickerValue}
                                onChange={(date) => handleDateChange(date, inputNameFrom)}
                                onAccept={(date) => handleDateAccept(date, true)}
                                disabled={disabled}
                                className={classes.datePicker}
                                slotProps={{
                                    ...dateInputSlotProps,
                                    textField: {
                                        ...dateInputSlotProps.textField,
                                        id: inputNameFrom,
                                        onFocus: () => setFromFocused(true),
                                        onBlur: () => handleFieldBlur(true),
                                        helperText: fromFieldError?.message,
                                        error: !!fromFieldError,
                                    },
                                    popper: dateInputSlotProps.popper,
                                }}
                            />
                        </Grid>
                        <Grid size={6}>
                            <InputLabel shrink focused={toFocused} htmlFor={inputNameTo}>
                                To
                            </InputLabel>
                            <DatePicker
                                name={inputNameTo}
                                data-testid={`${inputNameTo}-input`}
                                value={toPickerValue}
                                onChange={(date) => handleDateChange(date, inputNameTo)}
                                onAccept={(date) => handleDateAccept(date, false)}
                                disabled={disabled}
                                className={classes.datePicker}
                                slotProps={{
                                    ...dateInputSlotProps,
                                    textField: {
                                        ...dateInputSlotProps.textField,
                                        id: inputNameTo,
                                        onFocus: () => setToFocused(true),
                                        onBlur: () => handleFieldBlur(false),
                                        helperText: toFieldError?.message,
                                        error: !!toFieldError,
                                    },
                                    popper: dateInputSlotProps.popper,
                                }}
                            />
                        </Grid>
                    </Grid>
                </FormGroup>
            </FormControl>
        </LocalizationProvider>
    );
});

export default withStyles(styles)(ConnectedDateRangePicker);
